import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Widget, WidgetClickPoint } from '../types';
import { ChartBar } from './ChartBar';
import { ChartLine } from './ChartLine';
import { ChartPie } from './ChartPie';
import { ChartTable } from './ChartTable';
import { ChartMetric } from './ChartMetric';
import { ChartCardGrid } from './ChartCardGrid';
import { ChartOpinions } from './ChartOpinions';
import { ChartText } from './ChartText';
import { ChartMap } from './ChartMap';
import { ChartWordCloud } from './ChartWordCloud';
import { LoaderSmall } from '../../LoaderSmall/LoaderSmall';
import { useCurrentUser } from '../../../contexts/CurrentUser';
import { useDashboard } from '../contexts/DashboardContext';
import { ampli } from '../../../ampli';
import styles from './WidgetRenderer.module.scss';

interface WidgetRendererProps {
  widget: Widget;
  onSetFilter?: (parameterName: string, value: any) => void;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
}

function resolveClickUrl(template: string, point: WidgetClickPoint): string {
  let url = template
    .replace(/\{key\}/g, encodeURIComponent(String(point.key)))
    .replace(/\{label\}/g, encodeURIComponent(String(point.label)))
    .replace(/\{value\}/g, encodeURIComponent(String(point.value)));

  // Replace any remaining {column_name} placeholders from extra row data
  if (point.extra) {
    url = url.replace(/\{(\w+)\}/g, (match, col) => {
      if (col in point.extra!) {
        return encodeURIComponent(String(point.extra![col] ?? ''));
      }
      return match;
    });
  }

  return url;
}

/**
 * Resolve a value template for set_filter actions.
 * If the template is a single placeholder (e.g. "{key}"), returns the raw value
 * preserving its original type (number, array, etc.).
 * For composite templates (e.g. "{key}-{label}"), returns a string.
 */
function resolveFilterValue(template: string, point: WidgetClickPoint): any {
  // Single placeholder — return raw value to preserve type
  const singleMatch = template.match(/^\{(\w+)\}$/);
  if (singleMatch) {
    const name = singleMatch[1];
    if (name === 'key') return point.key;
    if (name === 'label') return point.label;
    if (name === 'value') return point.value;
    if (point.extra && name in point.extra) return point.extra[name];
  }

  // Composite template — resolve to string, then try JSON parse for structured values
  let result = template
    .replace(/\{key\}/g, String(point.key))
    .replace(/\{label\}/g, String(point.label))
    .replace(/\{value\}/g, String(point.value));

  if (point.extra) {
    result = result.replace(/\{(\w+)\}/g, (match, col) => {
      if (col in point.extra!) {
        return String(point.extra![col] ?? '');
      }
      return match;
    });
  }

  try {
    return JSON.parse(result);
  } catch {
    return result;
  }
}

export const WidgetRenderer: React.FC<WidgetRendererProps> = ({ widget, onSetFilter, page, pageSize, onPageChange }) => {
  const { t } = useTranslation();
  const [currentUser] = useCurrentUser();
  const { displayedDashboard, isEditing, updateWidget } = useDashboard();
  const isSuperAdmin = currentUser?.is_super_admin ?? false;
  const [showErrorDetails, setShowErrorDetails] = useState(false);

  const onPointClick = useCallback((point: WidgetClickPoint) => {
    const action = widget.config.clickAction;
    if (!action) return;

    if (action.type === 'url') {
      const url = resolveClickUrl(action.url, point);
      ampli.track({
        event_type: 'Widget click action',
        event_properties: {
          user_id: currentUser?.id,
          workspace_id: currentUser?.workspace_id,
          dashboard_id: displayedDashboard?.id,
          widget_id: widget.id,
          widget_type: widget.chartType,
          action_type: 'url',
          is_external: /^https?:\/\//i.test(url),
        },
      });
      window.open(url, action.target ?? '_blank');
    } else if (action.type === 'set_filter' && action.parameterName && onSetFilter) {
      const valueTemplate = action.valueTemplate ?? '{key}';
      const value = resolveFilterValue(valueTemplate, point);
      ampli.track({
        event_type: 'Widget click action',
        event_properties: {
          user_id: currentUser?.id,
          workspace_id: currentUser?.workspace_id,
          dashboard_id: displayedDashboard?.id,
          widget_id: widget.id,
          widget_type: widget.chartType,
          action_type: 'set_filter',
          filter_parameter: action.parameterName,
        },
      });
      onSetFilter(action.parameterName, value);
    }
  }, [widget.config.clickAction, widget.id, widget.chartType, currentUser, displayedDashboard?.id, onSetFilter]);

  const clickHandler = widget.config.clickAction ? onPointClick : undefined;

  // Word cloud, edit mode: turn the words the admin hid into permanent stop words.
  const saveWordCloudStopWords = useCallback((words: string[]) => {
    const wordCloudConfig = widget.config.wordCloudConfig ?? {};
    const known = new Set((wordCloudConfig.stopWords ?? []).map((w) => w.trim().toLowerCase()));
    const added = words.map((w) => w.trim()).filter((w) => w && !known.has(w.toLowerCase()));
    if (!added.length) return;
    updateWidget(widget.id, {
      config: {
        ...widget.config,
        wordCloudConfig: { ...wordCloudConfig, stopWords: [...(wordCloudConfig.stopWords ?? []), ...added] },
      },
    });
    toast.success(t('Stop words updated') as string);
  }, [widget.id, widget.config, updateWidget, t]);

  // Text widget renders from config only — no SQL data needed
  if (widget.chartType === 'text') {
    return <ChartText config={widget.config} />;
  }

  if (!widget.data) {
    return (
      <div className={styles.loaderWrapper}>
        <LoaderSmall />
      </div>
    );
  }

  if (widget.data.error) {
    return (
      <div className={styles.errorWrapper}>
        <span className={styles.errorIcon}>⚠</span>
        <p className={styles.errorMessage}>{t('Failed to load data. Please check the query configuration.')}</p>
        {isSuperAdmin && (
          <button className={styles.errorToggle} onClick={() => setShowErrorDetails((v) => !v)}>
            {showErrorDetails ? t('Hide SQL error') : t('Show SQL error')}
          </button>
        )}
        {isSuperAdmin && showErrorDetails && (
          <pre className={styles.errorDetails}>{widget.data.error}</pre>
        )}
      </div>
    );
  }

  if (widget.data.rows.length === 0) {
    return (
      <div className={styles.placeholder}>{t('No data to display')}</div>
    );
  }

  switch (widget.chartType) {
    case 'bar':
      return <ChartBar data={widget.data} config={widget.config} onPointClick={clickHandler} />;
    case 'line':
      return <ChartLine data={widget.data} config={widget.config} onPointClick={clickHandler} />;
    case 'pie':
      return <ChartPie data={widget.data} config={widget.config} onPointClick={clickHandler} />;
    case 'table':
      return (
        <ChartTable
          data={widget.data}
          config={widget.config}
          onPointClick={clickHandler}
          page={page}
          pageSize={pageSize}
          onPageChange={onPageChange}
        />
      );
    case 'metric':
      return <ChartMetric data={widget.data} config={widget.config} />;
    case 'card_grid':
      return <ChartCardGrid data={widget.data} config={widget.config} />;
    case 'opinions':
      return <ChartOpinions data={widget.data} config={widget.config} />;
    case 'map':
      return <ChartMap data={widget.data} config={widget.config} onPointClick={clickHandler} />;
    case 'word_cloud':
      return (
        <ChartWordCloud
          data={widget.data}
          config={widget.config}
          onPointClick={clickHandler}
          widgetId={widget.id}
          onSaveStopWords={isEditing ? saveWordCloudStopWords : undefined}
        />
      );
    default:
      return (
        <div className={styles.placeholder}>
          {t('Unknown chart type')}
        </div>
      );
  }
};
